/**
 * Jersey Handout — coach view.
 *
 * Access mirrors Check-In: pick one of your assigned classes, work the
 * roster. What differs is that a jersey is handed over once. There is no
 * record per session, so returning the following week shows exactly who is
 * still waiting rather than a blank sheet.
 *
 * Two rules from the club are enforced here and on the server:
 * players who did not purchase a jersey cannot be marked, and a coach has
 * 48 hours from marking to undo a mistake before an admin has to do it.
 */

import React, { useMemo, useRef, useState } from "react";
import { Search, Shirt, Undo2, Check, Ruler, X } from "lucide-react";
import toast from "react-hot-toast";

import Header from "../../components/Header";
import Footer from "../../components/Footer";

import { useAuth } from "../../context/auth";
import { useApi } from "../../hooks";

import { classesService } from "../../api/services";
import jerseyService from "../../api/services/jersey.service";
import {
  formatDate,
  isNewRegistration,
  sortClassesByName,
  JERSEY_SIZE_OPTIONS,
} from "../../utils/format";

/** "youth_m" reads as "Youth M" on the roster. */
const formatSize = (size) =>
  size
    ? size
        .split("_")
        .map((part) =>
          part === "youth" || part === "adult"
            ? part.charAt(0).toUpperCase() + part.slice(1)
            : part.toUpperCase(),
        )
        .join(" ")
    : "No size on file";

const JerseyHandout = () => {
  const { user } = useAuth();
  const [search, setSearch] = useState("");
  const [selectedClass, setSelectedClass] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [busyId, setBusyId] = useState(null);
  // Which player the coach is choosing a replacement size for.
  const [sizeRequestFor, setSizeRequestFor] = useState(null);
  const [requestedSize, setRequestedSize] = useState("");

  // Coach's assigned classes.
  const { data: classesData, loading: loadingClasses } = useApi(
    () => classesService.getAll({ coach_id: user?.id }),
    {
      initialData: { items: [] },
      dependencies: [user?.id],
      autoFetch: !!user?.id,
    },
  );

  const classes = useMemo(
    () =>
      sortClassesByName(
        Array.isArray(classesData) ? classesData : classesData?.items || [],
      ),
    [classesData],
  );

  React.useEffect(() => {
    if (classes.length > 0 && !selectedClass) {
      setSelectedClass(classes[0]);
    }
  }, [classes, selectedClass]);

  // Which class the roster on screen belongs to. The skeleton is only for a
  // class's FIRST load: a refetch after marking must keep the list mounted,
  // or the coach is thrown back to the top of the page on every tap.
  const [loadedClassId, setLoadedClassId] = useState(null);
  const requestedClassIdRef = useRef(null);
  requestedClassIdRef.current = selectedClass?.id || null;

  const {
    data: roster,
    loading: loadingRoster,
    refetch: refetchRoster,
  } = useApi(() => jerseyService.getRoster(selectedClass?.id), {
    initialData: { items: [], size_totals: {}, outstanding_count: 0 },
    dependencies: [selectedClass?.id],
    autoFetch: !!selectedClass?.id,
    onSuccess: () => setLoadedClassId(requestedClassIdRef.current),
    onError: (error) => console.error("Failed to load jersey roster:", error),
  });

  const isFirstLoadForClass =
    loadingRoster && loadedClassId !== (selectedClass?.id || null);

  const players = useMemo(() => {
    const items = roster?.items || [];
    if (!search.trim()) return items;
    const needle = search.trim().toLowerCase();
    return items.filter((p) => (p.child_name || "").toLowerCase().includes(needle));
  }, [roster, search]);

  const sizeTotals = Object.entries(roster?.size_totals || {}).sort(
    ([a], [b]) => a.localeCompare(b),
  );

  const handleClassChange = (classItem) => {
    setSelectedClass(classItem);
    setDropdownOpen(false);
  };

  const handleMark = async (player) => {
    if (busyId) return;
    try {
      setBusyId(player.enrollment_id);
      await jerseyService.markReceived(player.enrollment_id);
      toast.success(`${player.child_name} has their jersey.`);
      refetchRoster();
    } catch (error) {
      console.error("Failed to mark jersey received:", error);
      toast.error(
        error.response?.data?.message ||
          "Could not mark that jersey. Please try again.",
      );
    } finally {
      setBusyId(null);
    }
  };

  const handleUndo = async (player) => {
    if (busyId) return;
    try {
      setBusyId(player.enrollment_id);
      await jerseyService.undo(player.enrollment_id);
      toast.success(`Undone for ${player.child_name}.`);
      refetchRoster();
    } catch (error) {
      console.error("Failed to undo jersey handout:", error);
      toast.error(
        error.response?.data?.message ||
          "Could not undo that. Please try again.",
      );
    } finally {
      setBusyId(null);
    }
  };

  const openSizeRequest = (player) => {
    setSizeRequestFor(player);
    setRequestedSize("");
  };

  const submitSizeRequest = async () => {
    if (!sizeRequestFor || !requestedSize) return;
    try {
      setBusyId(sizeRequestFor.enrollment_id);
      await jerseyService.requestDifferentSize(
        sizeRequestFor.enrollment_id,
        requestedSize,
      );
      toast.success(
        `Size request sent for ${sizeRequestFor.child_name}. The office will post it.`,
      );
      setSizeRequestFor(null);
      setRequestedSize("");
      refetchRoster();
    } catch (error) {
      console.error("Failed to request a different size:", error);
      toast.error(
        error.response?.data?.message ||
          "Could not send that request. Please try again.",
      );
    } finally {
      setBusyId(null);
    }
  };

  const confirmReplacement = async (player) => {
    if (busyId) return;
    try {
      setBusyId(player.enrollment_id);
      await jerseyService.markSizeRequestReceived(player.size_request_id);
      toast.success(`${player.child_name} has their replacement.`);
      refetchRoster();
    } catch (error) {
      console.error("Failed to confirm the replacement:", error);
      toast.error(
        error.response?.data?.message ||
          "Could not confirm that. Please try again.",
      );
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="min-h-screen max-sm:h-fit bg-page-gradient max-sm:pb-20">
      <Header />

      <main className="px-6 py-8 max-xxl:py-5 max-sm:py-4 max-sm:px-3">
        <div className="flex max-sm:justify-between mb-6 max-xxl:mb-4 max-sm:items-center">
          <h1 className="text-fluid-2xl text-[#173151] font-kollektif font-normal leading-[1.002] tracking-[-0.02em]">
            Jersey Handout
          </h1>
        </div>

        {/* SEARCH + CLASS PICKER */}
        <div className="flex flex-col lg:flex-row gap-4 mb-6 max-xxl:mb-3 xl:p-6 px-4 py-4 bg-[#FFFFFF80] rounded-3xl">
          <div className="flex items-center bg-[#f9fafb] h-12 rounded-full shadow-sm max-xl:py-2 px-4 py-3 w-full">
            <Search size={18} className="text-[#7c7c7c] mr-2" />
            <input
              type="text"
              placeholder="Search students..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full bg-transparent outline-none text-sm text-gray-700 placeholder:text-[#7c7c7c]"
            />
          </div>

          <div className="flex items-center gap-3 w-full lg:w-auto">
            <div className="flex-1 lg:flex-none relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                disabled={loadingClasses}
                className="w-full lg:w-[316px] h-12 flex justify-between items-center gap-2
                           bg-[#FFFFFF66] px-6 py-[14px] rounded-full
                           shadow-sm text-fluid-base font-medium text-nuetral-200"
              >
                <span className="truncate text-left">
                  {loadingClasses
                    ? "Loading classes..."
                    : selectedClass
                      ? selectedClass.name
                      : "Select a class"}
                </span>
                <svg
                  className={`w-4 h-4 shrink-0 text-[#7c7c7c] transition-transform ${
                    dropdownOpen ? "rotate-180" : ""
                  }`}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              {dropdownOpen && classes.length > 0 && (
                <div className="absolute mt-0 w-full lg:w-[316px] max-h-72 overflow-y-auto z-40 bg-[#FFF] shadow-md border border-gray-200 rounded-xl">
                  {classes.map((classItem) => (
                    <button
                      key={classItem.id}
                      onClick={() => handleClassChange(classItem)}
                      className={`w-full text-nuetral-200 font-semibold border-b-border-light shadow-sm text-left px-6 py-4 text-[15px] max-xl:text-sm hover:bg-gray-100 ${
                        selectedClass?.id === classItem.id ? "bg-gray-50" : ""
                      }`}
                    >
                      {classItem.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* WHAT TO BRING — only jerseys still owed are counted. */}
        {!isFirstLoadForClass && selectedClass && (
          <div className="bg-white/60 rounded-3xl shadow-md backdrop-blur-md border p-4 lg:p-6 mb-6">
            <div className="flex items-center gap-2 mb-3">
              <Shirt size={18} className="text-[#173151]" />
              <h2 className="font-semibold text-[#1B1B1B] font-manrope">
                {roster?.outstanding_count || 0} still to hand out
              </h2>
            </div>
            {sizeTotals.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {sizeTotals.map(([size, count]) => (
                  <span
                    key={size}
                    className="inline-flex items-center gap-2 bg-[#173151]/10 text-[#173151] text-sm font-medium font-manrope px-3 py-1.5 rounded-full"
                  >
                    {formatSize(size)}
                    <strong>{count}</strong>
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500 font-manrope">
                Everyone in this class who bought a jersey has one.
              </p>
            )}
          </div>
        )}

        {/* ROSTER */}
        {isFirstLoadForClass ? (
          <div className="bg-white/60 p-4 lg:p-7 rounded-3xl shadow-md backdrop-blur-md border">
            <div className="animate-pulse space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <div
                  key={i}
                  className="flex items-center gap-4 p-4 bg-white/30 rounded-xl"
                >
                  <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded w-32 mb-2"></div>
                    <div className="h-3 bg-gray-200 rounded w-20"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : !selectedClass ? (
          <div className="bg-white/60 p-8 lg:p-12 rounded-3xl shadow-md backdrop-blur-md border text-center">
            <h3 className="text-lg font-semibold text-gray-600 mb-1">
              No Class Selected
            </h3>
            <p className="text-gray-500 text-sm">
              Please select a class from the dropdown above to view players.
            </p>
          </div>
        ) : players.length === 0 ? (
          <div className="bg-white/60 p-8 lg:p-12 rounded-3xl shadow-md backdrop-blur-md border text-center">
            <h3 className="text-lg font-semibold text-gray-600 mb-1">
              {search ? "No students found" : "No Students Enrolled"}
            </h3>
            <p className="text-gray-500 text-sm">
              {search
                ? `Nobody in this class matches "${search}".`
                : `No students are currently enrolled in ${selectedClass?.name || "this class"}.`}
            </p>
          </div>
        ) : (
          <div className="bg-white/60 p-4 lg:p-7 rounded-3xl shadow-md backdrop-blur-md border">
            <h2 className="font-semibold text-lg sm:text-xl font-manrope text-[#1B1B1B] mb-3">
              Players ({players.filter((p) => p.received).length}/
              {players.length})
            </h2>

            <div className="flex flex-col gap-2">
              {players.map((player) => (
                <div
                  key={player.enrollment_id}
                  className="flex items-center justify-between gap-3 bg-[#FFFFFF33] border border-[#FFFFFF33] rounded-[14px] px-4 py-3 lg:p-5 shadow-sm"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-[#173151] font-manrope">
                        {player.child_name}
                      </span>
                      {isNewRegistration(player.enrolled_at) && (
                        <span className="text-[11px] font-semibold uppercase tracking-wide bg-[#F3BC48] text-black px-2 py-0.5 rounded-full">
                          New
                        </span>
                      )}
                      {player.joined_after_handout && (
                        <span className="text-[11px] font-semibold bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full">
                          Joined after handout
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-3 mt-1 flex-wrap">
                      <span className="text-sm text-gray-600 font-manrope">
                        {formatSize(player.jersey_size)}
                      </span>
                      {player.bought_jersey ? (
                        <span className="text-xs font-medium text-green-700 bg-green-50 px-2 py-0.5 rounded-full">
                          Jersey fee paid
                        </span>
                      ) : (
                        <span className="text-xs font-medium text-gray-600 bg-gray-100 px-2 py-0.5 rounded-full">
                          No jersey purchased
                        </span>
                      )}
                    </div>

                    {player.received && (
                      <p className="text-xs text-gray-500 font-manrope mt-1">
                        <Check size={12} className="inline text-green-600" />{" "}
                        Received {formatDate(player.received_at)}
                        {player.marked_by_name
                          ? ` · marked by ${player.marked_by_name}`
                          : ""}
                      </p>
                    )}

                    {player.size_request_status === "requested" && (
                      <p className="text-xs text-amber-700 font-manrope mt-1">
                        {formatSize(player.requested_size)} replacement
                        requested · waiting on the office
                      </p>
                    )}
                    {player.size_request_status === "sent_out" && (
                      <p className="text-xs text-blue-700 font-manrope mt-1">
                        {formatSize(player.requested_size)} replacement posted ·
                        confirm below once the player has it
                      </p>
                    )}
                  </div>

                  <div className="shrink-0 flex flex-col items-end gap-2">
                    {/* A posted replacement needs confirming before anything
                        else: that is the step the office is waiting on. */}
                    {player.size_request_status === "sent_out" ? (
                      <button
                        type="button"
                        onClick={() => confirmReplacement(player)}
                        disabled={busyId === player.enrollment_id}
                        className="bg-[#173151] text-white text-sm font-semibold rounded-full px-5 py-2 shadow-md hover:bg-[#1f3d67] disabled:opacity-50 whitespace-nowrap"
                      >
                        {busyId === player.enrollment_id
                          ? "Saving..."
                          : "Replacement received"}
                      </button>
                    ) : !player.bought_jersey ? null : player.received ? (
                      <>
                        {player.can_undo ? (
                          <button
                            type="button"
                            onClick={() => handleUndo(player)}
                            disabled={busyId === player.enrollment_id}
                            className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#173151] border border-gray-300 rounded-full px-4 py-2 hover:bg-gray-50 disabled:opacity-50"
                          >
                            <Undo2 size={14} />
                            Undo
                          </button>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-green-700">
                            <Check size={16} />
                            Received
                          </span>
                        )}
                        {!player.size_request_status && (
                          <button
                            type="button"
                            onClick={() => openSizeRequest(player)}
                            className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#173151] underline whitespace-nowrap"
                          >
                            <Ruler size={12} />
                            Request different size
                          </button>
                        )}
                      </>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleMark(player)}
                        disabled={busyId === player.enrollment_id}
                        className="bg-[#173151] text-white text-sm font-semibold rounded-full px-5 py-2 shadow-md hover:bg-[#1f3d67] disabled:opacity-50"
                      >
                        {busyId === player.enrollment_id
                          ? "Saving..."
                          : "Received?"}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      {/* SIZE REQUEST — the coach picks the size the player actually needs. */}
      {sizeRequestFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <h2 className="text-lg font-semibold text-[#173151] font-manrope">
                  Request a different size
                </h2>
                <p className="text-sm text-gray-500 font-manrope mt-1">
                  {sizeRequestFor.child_name} · currently{" "}
                  {formatSize(sizeRequestFor.jersey_size)}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSizeRequestFor(null)}
                aria-label="Close"
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            </div>

            <label
              htmlFor="replacement-size"
              className="block text-sm font-medium text-gray-700 font-manrope mb-2"
            >
              Size needed
            </label>
            <select
              id="replacement-size"
              value={requestedSize}
              onChange={(e) => setRequestedSize(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-xl bg-white outline-none focus:ring-2 focus:ring-[#173151]"
            >
              <option value="">Choose a size</option>
              {JERSEY_SIZE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>

            <p className="text-xs text-gray-500 font-manrope mt-3">
              The office will post the replacement. You confirm here once the
              player has it. Their registered size stays as it is.
            </p>

            <div className="flex justify-end gap-3 mt-5">
              <button
                type="button"
                onClick={() => setSizeRequestFor(null)}
                className="px-4 py-2 rounded-xl text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitSizeRequest}
                disabled={!requestedSize || busyId !== null}
                className="px-5 py-2 rounded-xl text-sm font-semibold text-white bg-[#173151] hover:bg-[#1f3d67] disabled:opacity-50"
              >
                {busyId ? "Sending..." : "Send request"}
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default JerseyHandout;
